import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { Droplets, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={user.is_admin ? "/admin" : "/dashboard"} />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(`Welcome back, ${userData.name}`);
      navigate(userData.is_admin ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 relative noise-overlay" data-testid="login-page">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 30%, rgba(217, 70, 239, 0.06), transparent 60%)" }} />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-12">
          <a href="/" className="inline-flex items-center gap-2 group">
            <Droplets className="w-8 h-8 text-purple-500" />
            <span className="text-2xl font-bold tracking-[0.15em] uppercase neon-text-purple" style={{ fontFamily: "'Cinzel Decorative', serif" }}>P INK</span>
          </a>
        </div>
        <div className="glass-card p-8 rounded-xl animate-glow-pulse" data-testid="login-form-card">
          <h2 className="text-xl font-bold mb-2" data-testid="login-title">{t("login_title")}</h2>
          <p className="text-xs text-neutral-500 mb-6">{t("login_subtitle")}</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("login_email")}</Label>
              <Input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="username" className="bg-black/50 border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 text-white placeholder:text-neutral-600 h-12 rounded-lg" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wider uppercase text-neutral-400">{t("login_password")}</Label>
              <div className="relative">
                <Input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="***" className="bg-black/50 border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 text-white placeholder:text-neutral-600 h-12 rounded-lg pr-12" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors" data-testid="login-toggle-password">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-sm h-12" data-testid="login-submit-btn">
              {loading ? "..." : t("login_submit")}
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-neutral-600 mt-8">{t("login_no_account")}</p>
      </div>
    </div>
  );
}
